import React, { useState, useEffect } from 'react';
import { reportAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, XCircle, Clock, Loader2, User, Calendar, Briefcase, Timer } from 'lucide-react';
import { toast } from 'sonner';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

const TYPE_BADGE = {
  support: { bg: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Support' },
  meeting: { bg: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Meeting' },
  training: { bg: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Training' },
};

export default function Approvals() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await reportAPI.getPendingApprovals();
      setTasks(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleApprove = async (taskId, status) => {
    setProcessing(p => ({ ...p, [taskId]: status }));
    try {
      await reportAPI.approveTask(taskId, status);
      toast.success(`Task ${status}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${status} task`);
    } finally {
      setProcessing(p => ({ ...p, [taskId]: null }));
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-[#002FA7]" size={28} />
    </div>
  );

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto" data-testid="approvals-page">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-lg sm:text-xl font-bold text-slate-800">Pending Approvals</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">Review and approve support, meeting & training hours</p>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-slate-200" data-testid="no-approvals">
          <CheckCircle2 className="mx-auto text-green-400 mb-3" size={40} />
          <p className="text-slate-500 font-medium">All caught up!</p>
          <p className="text-sm text-slate-400 mt-1">No tasks pending approval</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="approval-list">
          <p className="text-sm text-slate-500 font-medium">{tasks.length} task{tasks.length !== 1 ? 's' : ''} awaiting your review</p>
          {tasks.map(task => {
            const badge = TYPE_BADGE[task.task_type] || TYPE_BADGE.support;
            const assignees = task.assignees || [];
            return (
              <div key={task.id} className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow" data-testid={`approval-card-${task.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.bg}`}>{badge.label}</span>
                      <span className="text-[10px] font-mono text-slate-400">{task.task_number}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-slate-800 truncate">{task.title}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Briefcase size={12} />
                        {task.project?.name || task.client?.name || '-'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {fmtDate(task.start_date)} — {fmtDate(task.due_date)}
                      </span>
                      {(task.estimated_hours || task.actual_hours) && (
                        <span className="flex items-center gap-1">
                          <Timer size={12} />
                          {task.estimated_hours ? `${task.estimated_hours}h est` : ''}{task.actual_hours ? ` / ${task.actual_hours}h actual` : ''}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2">
                      {assignees.slice(0, 3).map(a => (
                        <span key={a.id} className="inline-flex items-center gap-1 text-[10px] text-slate-500 bg-slate-50 rounded-full px-2 py-0.5">
                          <span className="w-4 h-4 rounded-full bg-slate-300 text-white text-[8px] font-bold flex items-center justify-center">{getInitials(a.name)}</span>
                          {a.name?.split(' ')[0]}
                        </span>
                      ))}
                      {assignees.length > 3 && <span className="text-[10px] text-slate-400">+{assignees.length - 3}</span>}
                      {task.created_by && (
                        <span className="text-[10px] text-slate-400 ml-2 flex items-center gap-1">
                          <User size={10} /> Created by {task.created_by.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleApprove(task.id, 'rejected')}
                      disabled={!!processing[task.id]}
                      className="h-8 px-3 text-xs font-medium rounded-md border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 flex items-center gap-1 w-full sm:w-auto justify-center"
                      data-testid={`reject-btn-${task.id}`}
                    >
                      {processing[task.id] === 'rejected' ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                      Reject
                    </button>
                    <button
                      onClick={() => handleApprove(task.id, 'approved')}
                      disabled={!!processing[task.id]}
                      className="h-8 px-3 text-xs font-semibold rounded-md text-white bg-[#002FA7] hover:bg-[#002585] disabled:opacity-50 flex items-center gap-1 w-full sm:w-auto justify-center"
                      data-testid={`approve-btn-${task.id}`}
                    >
                      {processing[task.id] === 'approved' ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                      Approve
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
